import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Box, AppBar, Toolbar, Typography, IconButton, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText, BottomNavigation, BottomNavigationAction, Paper, Divider, useMediaQuery, useTheme,  } from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Store as StoreIcon,
  CardMembership as SubscribersIcon,
  Payment as PaymentIcon,
  Assignment as RequestsIcon,
  Storefront as BrandIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useSetRecoilState } from 'recoil';
import { loggedInState, userInfoState } from '../recoil/atoms';

const drawerWidth = 250;
const GRADIENT = 'linear-gradient(45deg, #1a237e 30%, #3949ab 90%)';
const GOLD = '#ffd54f';

const menuItems = [
  { text: 'داشبورد', icon: <DashboardIcon />, path: '/admin' },
  { text: 'گالری پرده‌ها', icon: <StoreIcon />, path: '/admin/shops' },
  { text: 'مشترکین', icon: <SubscribersIcon />, path: '/admin/subscribers' },
  { text: 'پرداخت‌ها', icon: <PaymentIcon />, path: '/admin/payments' },
  { text: 'درخواست‌ها', icon: <RequestsIcon />, path: '/admin/requests' },
];

const AdminLayout = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const setLoggedIn = useSetRecoilState(loggedInState);
  const setUserInfo = useSetRecoilState(userInfoState);

  const handleLogout = () => {
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('user');
    setLoggedIn(false);
    setUserInfo(null);
    navigate('/login');
  };

  const getCurrentTab = () => {
    const index = menuItems.findIndex(item => item.path === location.pathname);
    return index >= 0 ? index : 0;
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: '#fafbff' }}>
      {/* هدر دراور */}
      <Box sx={{
        background: GRADIENT, color: 'white', px: 2, py: 2.5,
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <BrandIcon sx={{ color: GOLD, fontSize: 30 }} />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 'bold', lineHeight: 1.2 }}>
            گالری پرده
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.85 }}>
            پنل مدیریت
          </Typography>
        </Box>
      </Box>

      <List sx={{ px: 1, py: 2, flexGrow: 1 }}>
        {menuItems.map((item) => {
          const selected = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                selected={selected}
                onClick={() => {
                  navigate(item.path);
                  setDrawerOpen(false);
                }}
                sx={{
                  borderRadius: 2,
                  py: 1.1,
                  borderRight: selected ? `4px solid ${GOLD}` : '4px solid transparent',
                  bgcolor: selected ? 'rgba(26,35,126,0.08)' : 'transparent',
                  '&.Mui-selected': { bgcolor: 'rgba(26,35,126,0.08)' },
                  '&.Mui-selected:hover': { bgcolor: 'rgba(26,35,126,0.14)' },
                  '&:hover': { bgcolor: 'rgba(26,35,126,0.05)' },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: selected ? '#1a237e' : '#5c6bc0' }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    textAlign: 'right',
                    fontWeight: selected ? 'bold' : 500,
                    color: selected ? '#1a237e' : '#37474f',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />
      <List sx={{ px: 1, py: 1 }}>
        <ListItem disablePadding>
          <ListItemButton onClick={handleLogout} sx={{ borderRadius: 2, py: 1.1 }}>
            <ListItemIcon sx={{ minWidth: 40 }}><LogoutIcon color="error" /></ListItemIcon>
            <ListItemText
              primary="خروج"
              primaryTypographyProps={{ textAlign: 'right', fontWeight: 500 }}
              sx={{ color: 'error.main' }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mr: { md: `${drawerWidth}px` },
          background: GRADIENT,
          borderBottom: `3px solid ${GOLD}`,
        }}
      >
        <Toolbar>
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ ml: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>
            پنل ادمین
          </Typography>
          {!isMobile && (
            <IconButton color="inherit" onClick={handleLogout}>
              <LogoutIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      {/* Drawer - Desktop */}
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="right"
          sx={{
            width: drawerWidth,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              borderLeft: '1px solid rgba(0,0,0,0.08)',
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      {/* Drawer - Mobile */}
      <Drawer
        variant="temporary"
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': {
            width: drawerWidth,
          },
        }}
      >
        {drawer}
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: '64px',
          mb: { xs: '56px', md: 0 },
          bgcolor: '#f4f6fb',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>

      {/* Bottom Navigation - Mobile Only */}
      {isMobile && (
        <Paper
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
          }}
          elevation={3}
        >
          <BottomNavigation
            value={getCurrentTab()}
            onChange={(e, newValue) => {
              navigate(menuItems[newValue].path);
            }}
            showLabels
            sx={{
              '& .Mui-selected': { color: '#1a237e !important' },
              '& .MuiBottomNavigationAction-root': { minWidth: 'auto' },
            }}
          >
            {menuItems.map((item) => (
              <BottomNavigationAction
                key={item.text}
                label={item.text}
                icon={item.icon}
              />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

export default AdminLayout;